import { useStackNavigate } from '@/app/navigation/stack-links';
import {
  TokenizedInput,
  TokenizedInputRef,
} from '@/components/ui/TokenizedInput/TokenizedInput';
import { TokenDefinition } from '@/components/ui/TokenizedInput/types';
import { Button } from '@/components/ui/button';
import { useToggle } from '@/hooks/use-toggle';
import {
  ChevronsDownUp,
  ChevronsUpDown,
  FilterIcon,
  PlusIcon,
} from 'lucide-react';
import { useEffect, useRef } from 'react';
import {
  TableControlsButton,
  ContentState,
} from '@/components/ui/data-table/TableControlsButton';
import { Table } from '@tanstack/react-table';
import {
  allExpandedMap,
  isAllExpandableExpanded,
} from '@/components/ui/data-table/tree.utils';
import { cn } from '@/lib/utils';

interface TableSearchBarProps<TData = any> {
  isFiltering: boolean;
  resultCount: number;
  totalCount: number;
  entityName: string;
  filterValue: string;
  onFilterChange: (value: string | null) => void;
  tokenDefinitions: TokenDefinition[];
  /** Default filter value - used for reset functionality */
  defaultFilter?: string;
  addPath?: string;
  // onAdd?: () => void;
  addDisabled?: boolean;
  // Table controls props
  table?: Table<TData>;
  /** Show the expand/collapse-all control (tree-mode tables only). */
  treeMode?: boolean;
  tableControlsOpen?: boolean;
  onTableControlsOpenChange?: (open: boolean) => void;
  tableControlsContentState?: ContentState;
  onTableControlsContentStateChange?: (state: ContentState) => void;
  onTableReset?: () => void;
}

export function TableSearchBar<TData = any>({
  isFiltering,
  resultCount,
  totalCount,
  entityName,
  filterValue,
  onFilterChange,
  tokenDefinitions,
  defaultFilter,
  addPath,
  // onAdd,
  addDisabled = false,
  table,
  treeMode = false,
  tableControlsOpen,
  onTableControlsOpenChange,
  tableControlsContentState,
  onTableControlsContentStateChange,
  onTableReset,
}: TableSearchBarProps<TData>) {
  const [filterActive, toggleFilterActive] = useToggle(!!filterValue);
  const prevFilterActiveRef = useRef(filterActive);
  const tokenizedInputRef = useRef<TokenizedInputRef>(null);

  const nav = useStackNavigate();

  useEffect(() => {
    if (filterActive && !prevFilterActiveRef.current) {
      tokenizedInputRef.current?.focus();
    }
    prevFilterActiveRef.current = filterActive;
  }, [filterActive]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between">
        <div>
          <Button
            onClick={toggleFilterActive}
            className={cn(!isFiltering && 'text-muted-foreground')}
            variant={isFiltering ? 'default' : 'outline'}
            size="sm"
          >
            <FilterIcon className={isFiltering ? 'fill-current' : ''} />
            {isFiltering
              ? `${resultCount} of ${totalCount} ${entityName}`
              : `All ${totalCount} ${entityName}`}
          </Button>
        </div>
        <div className="flex gap-2">
          {treeMode && table && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                // Set an explicit id map rather than the tanstack `true`
                // sentinel, so a later single-row/subtree collapse can remove
                // ids (you can't delete out of a boolean).
                table.setExpanded(
                  isAllExpandableExpanded(table) ? {} : allExpandedMap(table),
                )
              }
              tooltip={
                isAllExpandableExpanded(table) ? 'Collapse all' : 'Expand all'
              }
            >
              {isAllExpandableExpanded(table) ? (
                <ChevronsDownUp />
              ) : (
                <ChevronsUpDown />
              )}
            </Button>
          )}
          {table && (
            <TableControlsButton
              table={table}
              open={tableControlsOpen}
              onOpenChange={onTableControlsOpenChange}
              contentState={tableControlsContentState}
              onContentStateChange={onTableControlsContentStateChange}
              onReset={onTableReset}
            />
          )}
          {addPath && (
            <Button
              disabled={addDisabled}
              variant="outline"
              size="sm"
              onClick={() => nav({ to: addPath })}
              tooltip="Create new ticket"
            >
              <PlusIcon /> Add
            </Button>
          )}
        </div>
      </div>
      {filterActive && (
        <TokenizedInput
          ref={tokenizedInputRef}
          value={filterValue}
          onChange={onFilterChange}
          tokenDefinitions={tokenDefinitions}
          defaultFilter={defaultFilter}
          onClear={() => onFilterChange('')}
          onReset={
            defaultFilter !== undefined ? () => onFilterChange(null) : undefined
          }
        />
      )}
    </div>
  );
}
